import { Field, Form, Formik } from 'formik';
import React from 'react';
import { Dashboard, DashboardBackButton, PageLayout } from '@components';
import { LeavingGuard } from '@components/utils/LeavingGuard';
import { SECTION_NAMES } from '@constants/sectionNames';
import { Accordion, Banner, BaseContent, Button, ButtonClean, Checkbox, EntityTableCell, Fieldset, HeadContent, List, Loader, Select, Subsection, Tag, useModalContext, } from '@fourthwall/components';
import { routing } from '@utils/routing';
import { ConfirmBar } from '../../../../modules/ConfirmBar';
import { signets } from '../../assets/signets';
import { AccordionHeading } from '../../components/AccordionHeading/AccordionHeading';
import { ConnectBar } from '../../components/ConnectBar/ConnectBar';
import { ProductsStatusModal } from '../../components/ProductsStatusModal/ProductsStatusModal';
import { EmptyProductsApprovalStatus } from '../../modules/EmptyProductsApprovalStatus';
import { ConfigureSchema } from './AppsMeta.consts';
import * as S from './AppsMeta.styled';
import { CheckoutUrlSetupModal } from './components/CheckoutUrlSetupModal/CheckoutUrlSetupModal';
import { useAppsMeta } from './hooks/useAppsMeta';
export const AppsMeta = () => {
    const { statusTagProps, connectBarProps, settings, products, configure, checkoutUrls } = useAppsMeta();
    const { open } = useModalContext();
    const renderConfigureContent = () => {
        if (!configure)
            return null;
        const { type } = configure;
        if (type === 'CONNECT_FIRST') {
            return React.createElement(BaseContent, null, "Connect your Meta account first.");
        }
        if (type === 'CREATE_SHOP') {
            return (React.createElement(React.Fragment, null,
                React.createElement(BaseContent, null, "Your Meta account doesn\u2019t have any shops created. Refresh this page after adding a Meta Shop to your account."),
                React.createElement(S.ActionContainer, null,
                    React.createElement(Button, { appearance: "primary", label: "Create a new Meta Shop", href: configure.url, target: "_blank" }))));
        }
        if (type === 'SELECT_SHOP') {
            return (React.createElement(Formik, { initialValues: configure.initialValues || { metaCatalogId: '' }, validationSchema: ConfigureSchema, validateOnMount: true, enableReinitialize: true, onSubmit: configure.onSubmit }, (formik) => (React.createElement(Form, null,
                React.createElement(Fieldset, null,
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(Field, { component: Select, name: "metaCatalogId", label: "Select your Meta Product Catalog", suggestion: React.createElement(React.Fragment, null,
                                    "Pick your Meta Product Catalog or",
                                    ' ',
                                    React.createElement("a", { href: configure.url, target: "_blank", rel: "noreferrer" }, "create a new shop"),
                                    "."), ...configure.selectProps }))),
                    configure.previewProps && (React.createElement(S.ConfigurePreviewContainer, null,
                        React.createElement(List, { data: [configure.previewProps], renderItem: (item) => (React.createElement(S.ConfigurePreviewContent, null,
                                React.createElement("div", null,
                                    configure.isSelectedCatalogUsedInAnotherShop && (React.createElement(Banner, { appearance: "danger", size: "small" }, "This catalog is used in another shop")),
                                    React.createElement(EntityTableCell, { top: item.catalogTitle, bottom: React.createElement(React.Fragment, null,
                                            React.createElement("div", null, item.catalogDescription),
                                            React.createElement(BaseContent, { size: "xsmall", appearance: "secondary" }, item.catalogFooter)) })))) }))),
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(Button, { type: "submit", label: "Confirm selection", appearance: "primary", disabled: !formik.isValid || configure.isSelectedCatalogUsedInAnotherShop, loading: formik.isSubmitting }))))))));
        }
    };
    const renderSettings = () => {
        if (!settings)
            return null;
        return (React.createElement(Formik, { initialValues: settings.initialValues, enableReinitialize: true, onSubmit: settings.onSubmit }, (formik) => (React.createElement(Subsection, { title: "Settings" },
            settings.banners.length > 0 && (React.createElement(S.BannersContainer, null, settings.banners.map((banner, index) => (React.createElement(Banner, { key: index, ...banner }))))),
            React.createElement(Form, null,
                React.createElement(Fieldset, null,
                    React.createElement(Fieldset.Row, null,
                        React.createElement(Fieldset.Item, null,
                            React.createElement(Field, { component: Checkbox, name: "status", disabled: settings.disabled, label: "Enable sales on Instagram/Facebook shop", description: "Turning this on will allow fans to purchase your Fourthwall products directly from your Instagram or Facebook shop." }))))),
            React.createElement(ConfirmBar, { isOpen: formik.dirty, isLoading: formik.isSubmitting, onConfirm: formik.submitForm, onCancel: formik.resetForm }),
            React.createElement(LeavingGuard, { when: formik.dirty, isValid: formik.isValid, onSave: formik.submitForm })))));
    };
    const renderMetaCheckoutUrl = () => {
        // Only show if we have the checkout URLs available
        if (!checkoutUrls)
            return null;
        return (React.createElement(Subsection, { title: "Meta checkout URL" },
            React.createElement(BaseContent, null,
                React.createElement("p", null, "If you haven't configured your Meta checkout URL you can start the process by clicking on a button below:")),
            React.createElement(S.ActionContainer, null,
                React.createElement(Button, { appearance: "secondary", label: "Configure Meta checkout URL", onClick: () => open(CheckoutUrlSetupModal, checkoutUrls) }))));
    };
    const renderProducts = () => {
        if (!products)
            return null;
        if (products.type === 'LOADING') {
            return (React.createElement(Subsection, { title: "Product sync status" },
                React.createElement(S.LoaderContainer, null,
                    React.createElement(Loader, { size: "large" }))));
        }
        if (products.type === 'EMPTY') {
            return React.createElement(EmptyProductsApprovalStatus, { appName: "Meta" });
        }
        const handleSeeDetails = (title, products) => {
            open(ProductsStatusModal, { title, products });
        };
        return (React.createElement(Subsection, { title: "Product sync status" },
            React.createElement(BaseContent, null,
                React.createElement("p", null, "Products that meet Meta's requirements are synced to your Instagram or Facebook shop. This process can take up to an hour.")),
            React.createElement(S.ProductStatusAccordion, null,
                React.createElement(Accordion, null, products.groups.map((group) => (React.createElement(Accordion.Item, { key: group.type, heading: React.createElement(AccordionHeading, { status: group.type, label: group.label, onSeeDetails: () => {
                            if (group.details.type === 'ITEMS') {
                                return handleSeeDetails(group.label, group.details.items);
                            }
                            if (group.details.type === 'LINK') {
                                return window.open(group.details.url, '_blank');
                            }
                        } }) })))))));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(HeadContent, { title: SECTION_NAMES.appMeta }),
        React.createElement(PageLayout, { size: "tiny" },
            React.createElement(Dashboard.Section, { name: SECTION_NAMES.appMeta, topContent: React.createElement(DashboardBackButton, { label: SECTION_NAMES.apps, to: routing.apps.self }) },
                React.createElement(Subsection, null,
                    React.createElement(S.SubsectionContent, null,
                        React.createElement("div", null, statusTagProps && React.createElement(Tag, { ...statusTagProps, size: "large", display: "inline-flex" })),
                        React.createElement("div", null,
                            React.createElement(BaseContent, null,
                                "Showcase and sell products directly to your fans on Instagram or Facebook.",
                                ' ',
                                React.createElement(ButtonClean, { label: "Learn More", href: routing.external.helpCenter.meta, target: "_blank" }))),
                        connectBarProps && React.createElement(ConnectBar, { ...connectBarProps, logoSrc: signets.metaSignet }))),
                configure && (React.createElement(Subsection, { title: "Configure your Instagram or Facebook Shop" }, renderConfigureContent())),
                renderSettings(),
                renderMetaCheckoutUrl(),
                renderProducts()))));
};
